<template>
  <div class="contract-view">
    <div class="container">
      <Loader
        v-if="$apollo.queries.contract.loading"
        color="#FF035A"
        size="50"
      />
      <div v-else-if="error" class="error-container">
        <h2>Error</h2>
        <p>{{ error }}</p>
        <button @click="goBack" class="primary">Go Back</button>
      </div>
      <div v-else-if="contract" class="contract-container">
        <div class="header">
          <h1>{{ recordTitle }}</h1>
          <div class="actions">
            <button
              @click="downloadPdf"
              class="primary"
              :disabled="isDownloading"
            >
              {{ isDownloading ? "Downloading..." : "Download PDF" }}
            </button>
            <button @click="goBack" class="primary outline">Back</button>
          </div>
        </div>

        <ContractPresentation
          v-if="!contract.contract_url"
          :font.sync="pdfFont"
          :bold-values.sync="boldValues"
        />
        <div
          v-if="
            (contract.generated_text || !contract.contract_url) &&
              !contractIsResolved
          "
          class="contract-resolution-error"
          role="alert"
        >
          This saved record contains unresolved fields. Download it for
          reference; downloading does not repair or finalize the agreement.
          <span v-if="hasSignatures">
            Existing signatures are preserved. Corrections require a separate
            amendment.
          </span>
          <ul v-if="unresolvedFields.length">
            <li v-for="(field, index) in unresolvedFields" :key="index">
              {{ field.title }}: {{ field.excerpt }}
            </li>
          </ul>
        </div>

        <div
          id="contract-content-for-pdf"
          class="contract-content"
          :class="{
            'contract-content--modern': pdfFont === 'modern',
            'contract-content--plain': !boldValues
          }"
        >
          <div class="letterhead">
            <img :src="contractLogo" alt="Nvoko" class="letterhead__mark" />
          </div>
          <div class="text" v-html="contractHtml"></div>
          <div v-if="hasSignatures" class="signatures">
            <div>
              <span
                class="signature"
                :style="`font-family: ${host_signature.font}, cursive;`"
              >
                {{ host_signature.name }}
              </span>
              <span class="party-name">Host</span>
              <span v-if="host_signature.id" class="signature-id"
                >ID: {{ host_signature.id }}</span
              >
            </div>
            <div>
              <span
                class="signature"
                :style="`font-family: ${guest_signature.font}, cursive;`"
              >
                {{ guest_signature.name }}
              </span>
              <span class="party-name">Guest</span>
              <span v-if="guest_signature.id" class="signature-id"
                >ID: {{ guest_signature.id }}</span
              >
            </div>
          </div>
        </div>

        <!-- Post-Contract Checklist - Only show when contract is fully signed -->
        <!-- <div v-if="hasSignatures" class="post-contract-section">
          <PostContractChecklist
            :deal-id="contractId"
            @checklist-updated="handleChecklistUpdate"
          />
        </div> -->

        <!-- Loading overlay for PDF generation -->
        <div v-if="isDownloading" class="loading-overlay">
          <Loader color="#FF035A" size="50" />
          <p>Generating PDF...</p>
        </div>
      </div>
      <div v-else class="error-container">
        <h2>Contract Not Found</h2>
        <p>The requested contract could not be found.</p>
        <button @click="goBack" class="primary">Go Back</button>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from "@/components/Loader.vue";
import ContractPresentation from "@/components/ContractPresentation.vue";
import contractLogo from "@/assets/logo-black.svg";
import GET_CONTRACT from "@/api/queries/GET_CONTRACT.gql";
import contractPunctuation from "@/utils/contractPunctuation";
import contractFilename from "@/utils/contractFilename";
import {
  selectRecordedContract,
  unresolvedClauses
} from "@/utils/contractRecovery";
import contractReadiness from "@/utils/contractReadiness";
import contractPdf from "@/utils/contractPdf";
import {
  loadModernPdfFonts,
  loadSignaturePdfFonts
} from "@/utils/contractPdfFonts";
import contractPdfContent from "@/utils/contractPdfContent";

export default {
  name: "ContractView",
  components: {
    Loader,
    ContractPresentation
  },
  data() {
    return {
      contractLogo,
      pdfFont: this.$route.query.font === "serif" ? "serif" : "modern",
      boldValues: this.$route.query.bold !== "0",
      error: null,
      isDownloading: false
    };
  },
  computed: {
    contractId() {
      return this.$route.params.id;
    },
    recordTitle() {
      const title =
        (this.contract &&
          this.contract.deal &&
          this.contract.deal.track_title) ||
        contractFilename.inferContractTitle(
          this.contract && this.contract.generated_text
        );
      return title
        ? `${contractFilename.decodeContractTitle(title)} — saved contract`
        : "Saved contract";
    },
    contractHtml() {
      return contractPunctuation.collapseSpaceBeforePunctuation(
        this.contract && this.contract.generated_text
      );
    },
    downloadFilename() {
      const title =
        (this.contract &&
          this.contract.deal &&
          this.contract.deal.track_title) ||
        contractFilename.inferContractTitle(
          this.contract && this.contract.generated_text
        );
      return contractFilename.buildContractFilename(
        title,
        this.contract && this.contract.signatures
      );
    },
    unresolvedFields() {
      return unresolvedClauses(this.contract && this.contract.generated_text);
    },
    contractIsResolved() {
      return contractReadiness.isContractResolved(
        this.contract && this.contract.generated_text
      );
    },
    hasSignatures() {
      return (
        this.contract &&
        this.contract.signatures &&
        this.contract.signatures.length > 0
      );
    },
    host_signature() {
      if (!this.hasSignatures) return { name: "", font: "Pacifico", id: null };

      const signature = this.contract.signatures.find(function(sig) {
        return sig.party === "host" && sig.type === "contract";
      });
      return signature
        ? { ...signature.value, id: signature.id }
        : { name: "", font: "Pacifico", id: null };
    },
    guest_signature() {
      if (!this.hasSignatures) return { name: "", font: "Pacifico", id: null };

      const signature = this.contract.signatures.find(function(sig) {
        return sig.party === "guest" && sig.type === "contract";
      });
      return signature
        ? { ...signature.value, id: signature.id }
        : { name: "", font: "Pacifico", id: null };
    }
  },
  apollo: {
    contract: {
      query: GET_CONTRACT,
      variables() {
        return {
          deal_id: this.contractId
        };
      },
      update(data) {
        return data.contracts && data.contracts.length > 0
          ? selectRecordedContract(data.contracts)
          : null;
      },
      error(error) {
        this.error = error.message;
      }
    }
  },
  methods: {
    async downloadPdf() {
      if (!this.contract) {
        this.error = "Contract not available for download";
        return;
      }

      if (
        this.contract.contract_url &&
        /^https?:\/\//i.test(this.contract.contract_url)
      ) {
        window.open(
          this.contract.contract_url,
          "_blank",
          "noopener,noreferrer"
        );
        return;
      }
      this.isDownloading = true;

      try {
        const element = document.getElementById("contract-content-for-pdf");
        if (!element) {
          throw new Error("Contract content element not found");
        }

        const logoElement = element.querySelector(".letterhead__mark");
        if (!logoElement.complete) {
          await logoElement.decode();
        }
        const logoWidth = logoElement.naturalWidth;
        const logoHeight = logoElement.naturalHeight;
        const logoCanvas = document.createElement("canvas");
        logoCanvas.width = logoWidth * 3;
        logoCanvas.height = logoHeight * 3;
        logoCanvas
          .getContext("2d")
          .drawImage(logoElement, 0, 0, logoCanvas.width, logoCanvas.height);
        const logoDataUrl = logoCanvas.toDataURL("image/png");
        const textRoot = element.querySelector(".text");
        const blocks = contractPdfContent.collectContractBlocks(textRoot);
        const fonts =
          this.pdfFont === "modern" ? await loadModernPdfFonts() : undefined;

        const signatures = this.hasSignatures
          ? [
              { ...this.host_signature, party: "HOST" },
              { ...this.guest_signature, party: "GUEST" }
            ]
          : [];
        const signatureFonts = await loadSignaturePdfFonts(signatures);
        const pdf = contractPdf.buildContractPdf({
          blocks,
          font: this.pdfFont,
          boldValues: this.boldValues,
          fonts,
          signatures,
          signatureFonts,
          logoDataUrl,
          logoRatio: logoCanvas.height / logoCanvas.width
        });
        pdf.save(this.downloadFilename);
      } catch (err) {
        console.error("PDF generation error:", err);
        this.error = "Error generating PDF: " + err.message;
      } finally {
        this.isDownloading = false;
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    handleChecklistUpdate(data) {
      // Handle checklist updates - could save to backend here
      console.log("Checklist updated:", data);
      // You could emit an event or call an API to save the checklist state
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/styles/_mixins.scss";

.contract-view {
  width: 100%;
  height: 100vh;
  display: flex;
  justify-content: center;
  padding: 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  overflow: hidden;
}

.container {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  overflow: hidden;
}

.error-container {
  text-align: center;
  padding: 3rem;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;

  h2 {
    color: #ff035a;
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 2rem;
  }

  button {
    min-width: 120px;
  }
}

.contract-content .text {
  ::v-deep h1,
  ::v-deep h2,
  ::v-deep h3,
  ::v-deep h4,
  ::v-deep h5,
  ::v-deep h6 {
    margin: 10px 0 6px 0 !important;
    padding: 0 !important;
  }

  ::v-deep h1 {
    font-size: 1.6em !important;
    color: #111827 !important;
    text-align: left !important;
    font-weight: 600 !important;
    letter-spacing: 0.2px !important;
    border: none !important;
  }

  ::v-deep h2 {
    font-size: 1.2em !important;
    color: #1f2933 !important;
    font-weight: 600 !important;
    text-transform: none !important;
    letter-spacing: 0.1px !important;
    border: none !important;
  }

  ::v-deep h3 {
    font-size: 1.1em !important;
    color: #27303b !important;
    font-weight: 500 !important;
    text-decoration: none !important;
  }

  ::v-deep h4,
  ::v-deep h5,
  ::v-deep h6 {
    font-size: 1em !important;
    color: #2c3e50 !important;
    font-weight: 500 !important;
  }
}

.contract-resolution-error {
  width: min(736px, calc(100% - 32px));
  margin: 16px auto 0;
  padding: 12px 16px;
  border: 1px solid #f0a8a8;
  border-radius: 8px;
  background: #fff4f4;
  color: #8f1d1d;
  font-size: 14px;
  line-height: 1.5;
}

.contract-container {
  width: 100%;
  height: 100%;
  background: white;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .header {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;

    h1 {
      font-size: 1.25rem;
      font-weight: 500;
      margin: 0;
      min-width: 0;
      overflow-wrap: anywhere;
    }

    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 1rem;

      button {
        min-width: 120px;
      }
    }
  }

  .contract-content {
    padding: 3rem 4rem;
    overflow-y: auto;
    background-color: #fff;
    font-family: "Georgia", "Times New Roman", serif;
    flex: 1;
    max-width: 800px;
    margin: 0 auto;
    line-height: 1.8;
    box-shadow: none;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    margin-top: 1rem;

    .letterhead {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
      padding-bottom: 1.25rem;
      border-bottom: 1px solid #e5e7eb;

      &__mark {
        width: 32px;
        height: 32px;
        object-fit: contain;
      }

      &__text {
        display: flex;
        flex-direction: column;
        line-height: 1.2;
      }

      &__name {
        font-size: 0.85rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #111827;
      }

      &__descriptor {
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: #6b7280;
      }
    }

    .text {
      color: #2c3e50;
      font-size: 16px;

      p {
        padding: 0.5rem 0;
        line-height: 1.7;
        margin-bottom: 1.25rem;
        text-align: justify;
        text-indent: 1.5rem;

        &:first-child {
          text-indent: 0;
        }

        // Reduce spacing after headings
        h1 + &,
        h2 + &,
        h3 + & {
          margin-top: 0.5rem;
        }
      }

      h1 {
        font-size: 1.6em !important;
        margin: 1.25rem 0 0.75rem 0 !important;
        padding: 0 !important;
        color: #111827 !important;
        text-align: left !important;
        font-weight: 600 !important;
        letter-spacing: 0.2px !important;
        border: none !important;
      }

      h2 {
        font-size: 1.2em !important;
        margin: 1.25rem 0 0.75rem 0 !important;
        padding: 0 !important;
        color: #1f2933 !important;
        font-weight: 600 !important;
        text-transform: none !important;
        letter-spacing: 0.1px !important;
        border: none !important;
      }

      h3 {
        font-size: 1.1em !important;
        margin: 1rem 0 0.5rem 0 !important;
        padding: 0 !important;
        color: #27303b !important;
        font-weight: 500 !important;
        text-decoration: none !important;
      }

      h4,
      h5,
      h6 {
        font-size: 1em !important;
        margin: 0.85rem 0 0.4rem 0 !important;
        padding: 0 !important;
        color: #2c3e50 !important;
        font-weight: 500 !important;
      }

      ul,
      ol {
        margin: 1rem 0 1rem 2.25rem;
        padding: 0;

        li {
          margin-bottom: 0.6rem;
          line-height: 1.7;
          text-align: justify;
        }

        ul,
        ol {
          margin: 0.4rem 0 0.4rem 2rem;
        }
      }

      strong,
      b {
        color: #1a252f;
        font-weight: 600;
      }

      em,
      i {
        font-style: italic;
        color: #34495e;
      }

      // Contract-specific styling
      .clause {
        margin: 2rem 0;
        padding: 1rem 0;
        border-top: 1px solid #ecf0f1;

        &:first-child {
          border-top: none;
        }
      }

      .section-number {
        font-weight: bold;
        color: #111827;
        margin-right: 0.5rem;
      }

      // Table styling if present
      table {
        width: 100%;
        border-collapse: collapse;
        margin: 1.5rem 0;

        th,
        td {
          border: 1px solid #bdc3c7;
          padding: 0.75rem;
          text-align: left;
        }

        th {
          background-color: #f8f9fa;
          font-weight: 600;
          color: #2c3e50;
        }
      }

      // Blockquote styling
      blockquote {
        border-left: 4px solid #e74c3c;
        margin: 1.5rem 0;
        padding: 1rem 1.5rem;
        background-color: #f8f9fa;
        font-style: italic;
        color: #34495e;
      }
    }

    .signatures {
      margin: 2.5rem 0 1rem 0;
      padding: 1.5rem 0 0 0;
      border-top: 1px solid #d6d9de;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 2rem;
      background-color: transparent;
      border-radius: 0;

      div {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        min-width: 0;

        .signature {
          width: 100%;
          font-size: 1.75rem;
          margin-bottom: 0.5rem;
          padding: 0.25rem 0;
          border: none;
          border-bottom: 1px solid #1f2933;
          color: #111827;
          min-height: 48px;
          display: flex;
          align-items: center;
        }

        .party-name {
          font-size: 0.8rem;
          color: #4b5563;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-top: 0.75rem;
        }

        .signature-id {
          font-size: 0.65rem;
          color: #6b7280;
          font-family: "Courier New", monospace;
          margin-top: 0.2rem;
          opacity: 0.8;
        }
      }
    }
  }

  .post-contract-section {
    max-width: 800px;
    margin: 2rem auto 0 auto;
    padding: 0 1rem;
  }
}

// Responsive design
@media (max-width: 768px) {
  .container {
    padding: 0;
  }

  .contract-container {
    .header {
      padding: 1rem;
      flex-direction: column;
      gap: 1rem;

      .actions {
        width: 100%;
        justify-content: center;

        button {
          flex: 1;
          max-width: 150px;
        }
      }
    }

    .contract-content {
      padding: 1.5rem;
      font-size: 14px;

      .text {
        h1 {
          font-size: 1.45em !important;
          margin: 1rem 0 0.6rem 0 !important;
          padding: 0 !important;
        }

        h2 {
          font-size: 1.15em !important;
          margin: 1rem 0 0.5rem 0 !important;
          padding: 0 !important;
        }

        h3 {
          padding: 0 !important;
        }

        h4,
        h5,
        h6 {
          padding: 0 !important;
        }

        p {
          text-indent: 0;
          text-align: left;
        }

        ul,
        ol {
          margin: 1rem 0 1rem 1.5rem;
        }
      }

      .signatures {
        grid-template-columns: 1fr;
        gap: 1.5rem;

        div {
          .signature {
            font-size: 1.5rem;
          }
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .contract-content {
    padding: 1rem !important;

    .text {
      font-size: 13px;

      h1 {
        font-size: 1.4em !important;
        padding: 0 !important;
      }

      h2 {
        font-size: 1.1em !important;
        padding: 0 !important;
      }

      h3 {
        padding: 0 !important;
      }

      h4,
      h5,
      h6 {
        padding: 0 !important;
      }
    }

    .signatures {
      div {
        .signature {
          font-size: 1.4rem;
        }
      }
    }
  }
}

// Specific styles for PDF generation
#contract-content-for-pdf {
  width: 100%;
  box-sizing: border-box;

  // Ensure proper rendering of content in PDF
  img {
    max-width: 100%;
    height: auto;
  }

  .letterhead {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    padding-bottom: 1rem;
    // border-bottom: 1px solid #e5e7eb;

    &__mark {
      width: 60px;
      height: 28px;
      object-fit: contain;
    }

    &__text {
      display: flex;
      flex-direction: column;
      line-height: 1.2;
    }

    &__name {
      font-size: 0.85rem;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-weight: 600;
      color: #111827;
    }

    &__descriptor {
      font-size: 0.7rem;
      text-transform: uppercase;
      letter-spacing: 0.12em;
      color: #6b7280;
    }
  }

  // Ensure proper page breaks in PDF
  .page-break {
    page-break-after: always;
  }

  // PDF-specific typography adjustments
  .text {
    font-size: 12pt;
    line-height: 1.6;
    letter-spacing: normal;
    margin: 0;
    padding: 0;

    h1 {
      font-size: 16pt;
      page-break-after: avoid;
      letter-spacing: normal;
      margin: 15pt 0 10pt 0;
      padding: 0;
      text-align: left;
    }

    h2 {
      font-size: 13pt;
      page-break-after: avoid;
      letter-spacing: normal;
      margin: 12pt 0 8pt 0;
      padding: 0;
      text-transform: none;
    }

    h3 {
      font-size: 12pt;
      page-break-after: avoid;
      letter-spacing: normal;
      margin: 10pt 0 6pt 0;
      padding: 0;
    }

    h4,
    h5,
    h6 {
      font-size: 11pt;
      page-break-after: avoid;
      letter-spacing: normal;
      margin: 8pt 0 4pt 0;
      padding: 0;
    }

    p {
      orphans: 2;
      widows: 2;
      margin: 0 0 8pt 0;
      padding: 2pt 0;
      letter-spacing: normal;
      text-align: justify;
    }

    ul,
    ol {
      margin: 8pt 0 8pt 20pt;
      padding: 0;
    }

    li {
      margin: 0 0 4pt 0;
      letter-spacing: normal;
      line-height: 1.5;
    }
  }
}

@media screen {
  #contract-content-for-pdf .letterhead__mark {
    width: 100px;
    height: auto;
  }
}

// Print styles
@media print {
  .contract-view {
    background-color: white;
  }

  .contract-container {
    .header {
      display: none;
    }

    .contract-content {
      box-shadow: none;
      border-radius: 0;
      margin-top: 0;
      padding: 1rem;

      .text {
        font-size: 11pt;

        h1 {
          font-size: 14pt;
          margin: 12pt 0 8pt 0;
          padding: 0;
          color: black;
          border: none;
          text-align: left;
        }

        h2 {
          font-size: 12pt;
          margin: 10pt 0 6pt 0;
          padding: 0;
          color: black;
          border: none;
        }

        h3 {
          padding: 0;
          color: black;
        }

        h4,
        h5,
        h6 {
          padding: 0;
          color: black;
        }

        p,
        li {
          color: black;
        }
      }

      .signatures {
        background-color: white !important;
        border-top: 1pt solid black !important;
        margin: 18pt 0 10pt 0 !important;
        padding: 10pt 0 0 0 !important;
        display: grid !important;
        grid-template-columns: repeat(auto-fit, minmax(160pt, 1fr)) !important;
        gap: 16pt !important;
        page-break-inside: avoid !important;

        div {
          display: flex !important;
          flex-direction: column !important;
          align-items: flex-start !important;
          min-width: 0 !important;

          .signature {
            color: black !important;
            border-bottom: 1pt solid black !important;
            font-size: 14pt !important;
            margin-bottom: 4pt !important;
            padding: 2pt 0 !important;
            min-height: 28pt !important;
            display: flex !important;
            align-items: center !important;
            width: 100% !important;
          }

          .party-name {
            color: black !important;
            font-size: 8pt !important;
            font-weight: 600 !important;
            text-transform: uppercase !important;
            letter-spacing: 1pt !important;
            margin-top: 5pt !important;
          }
        }
      }
    }
  }

  .loading-overlay {
    display: none;
  }
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  p {
    margin-top: 1rem;
    font-size: 1.2rem;
    font-weight: 500;
  }
}
</style>
